import { useCallback, useEffect, useRef, useState } from "react";
import Badge from "../components/common/Badge";
import Button from "../components/common/Button";
import PageHeader from "../components/common/PageHeader";
import RetryBlock from "../components/common/RetryBlock";
import AssignCampaignModal from "../components/team/AssignCampaignModal";
import ToggleStatusConfirmModal from "../components/team/ActivateConfirmModal";
import TeamActionMenu from "../components/team/TeamActionMenu";
import TeamMemberFormModal from "../components/team/TeamMemberFormModal";
import useApiRequest from "../hooks/useApiRequest";
import useModalState from "../hooks/useModalState";
import useShellContext from "../layout/useShellContext";
import { getCampaigns } from "../services/campaignsApi";
import {
  assignCall,
  getCalls,
  getAssignableCallingAgents,
} from "../services/callsApi";
import {
  addTeamMember,
  assignTeamCampaign,
  getTeamMembers,
  toggleTeamMemberStatus,
  updateTeamMember,
} from "../services/teamApi";
import { TEAM_PAGE_SIZE } from "../utils/constants";
import { normalizeApiError } from "../utils/errors";
import { formatNow } from "../utils/formatters";

const UNASSIGNED_DISPOSITIONS = [
  "interested",
  "callback_requested",
  "callback_required",
  "follow_up_needed",
  "follow_up",
];

function getStatusBadgeVariant(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "booked";
  if (s === "in call") return "purple";
  if (s === "break") return "requested";
  return "cancelled";
}

function dispositionBadge(disposition) {
  if (!disposition) return <span className="text-muted">—</span>;
  const label = disposition.replace(/_/g, " ");
  const map = {
    interested: "badge-success",
    callback_requested: "badge-warning",
    callback_required: "badge-warning",
    follow_up_needed: "badge-info",
    follow_up: "badge-info",
  };
  return <span className={`badge ${map[disposition] || "badge-secondary"}`}>{label}</span>;
}

export default function TeamManagementPage() {
  const { registerPageControls, resetPageControls, showToast } = useShellContext();

  // Team members state
  const { data: teamData, setData: setTeamData, error: teamError, run: runTeam, loading: teamLoading } =
    useApiRequest({ members: [], campaigns: [] });

  // Unassigned AI calls state
  const { data: callsData, setData: setCallsData, error: callsError, run: runCalls } =
    useApiRequest({ items: [], agents: [] });

  const [busyKey, setBusyKey] = useState("");
  const [openMenuMemberId, setOpenMenuMemberId] = useState("");
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [assigningCallId, setAssigningCallId] = useState(null);
  const [assignAgentId, setAssignAgentId] = useState("");

  const mountedRef = useRef(false);
  const formModal = useModalState({ mode: "add", member: null });
  const assignModal = useModalState(null);
  const toggleStatusModal = useModalState(null);

  const loadTeamData = useCallback(async ({ signal } = {}) => {
    const [membersResponse, campaignsResponse] = await Promise.all([
      getTeamMembers({ signal }),
      getCampaigns({ signal }),
    ]);
    return {
      members: Array.isArray(membersResponse) ? membersResponse : [],
      campaigns: Array.isArray(campaignsResponse)
        ? campaignsResponse
        : campaignsResponse?.campaigns || [],
    };
  }, []);

  const loadCallsData = useCallback(async ({ signal } = {}) => {
    const [callsResponse, agentsResponse] = await Promise.all([
      getCalls({ view: "canonical", status: "completed", limit: 50 }, { signal }),
      getAssignableCallingAgents({ signal }),
    ]);
    const allCalls = Array.isArray(callsResponse)
      ? callsResponse
      : callsResponse?.calls || callsResponse?.items || [];

    const unassigned = allCalls.filter(
      (c) =>
        !c.agent_id &&
        (UNASSIGNED_DISPOSITIONS.includes(c.disposition) ||
          Number(c.lead_score || 0) >= 70)
    );

    return {
      items: unassigned,
      agents: Array.isArray(agentsResponse) ? agentsResponse : [],
    };
  }, []);

  const loadAll = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (trackRefresh && mountedRef.current) setShellRefreshing(true);
      try {
        await Promise.all([runTeam(loadTeamData), runCalls(loadCallsData)]);
        if (mountedRef.current) setLastUpdated(formatNow());
      } finally {
        if (trackRefresh && mountedRef.current) setShellRefreshing(false);
      }
    },
    [loadTeamData, loadCallsData, runTeam, runCalls]
  );

  const handleRefresh = useCallback(() => loadAll({ trackRefresh: true }), [loadAll]);

  useEffect(() => {
    mountedRef.current = true;
    loadAll().catch(() => undefined);
    return () => { mountedRef.current = false; };
  }, [loadAll]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });
    return () => resetPageControls();
  }, [handleRefresh, lastUpdated, registerPageControls, resetPageControls, shellRefreshing]);

  const runTeamMutation = useCallback(
    async ({ key, action, successMessage, afterSuccess, resetPage = false }) => {
      setBusyKey(key);
      try {
        const response = await action();
        const nextMembers = response?.members || teamData?.members || [];
        setTeamData((current) => ({ ...current, members: nextMembers }));
        setLastUpdated(formatNow());
        if (resetPage) setCurrentPage(1);
        if (typeof afterSuccess === "function") afterSuccess(response);
        if (successMessage) showToast(successMessage, false, 4000);
      } catch (err) {
        const e = normalizeApiError(err);
        if (!e.isAbort) showToast(e.message, true, 5000);
      } finally {
        setBusyKey("");
      }
    },
    [teamData?.members, setTeamData, showToast]
  );

  async function handleAssign(callId) {
    if (!assignAgentId) {
      showToast("Please select an agent first", true, 3000);
      return;
    }
    setBusyKey(`assign-${callId}`);
    try {
      await assignCall(callId, { agent_id: Number(assignAgentId) });
      showToast("Call assigned successfully", false, 4000);
      setCallsData((prev) => ({
        ...prev,
        items: prev.items.filter((c) => c.call_id !== callId && c.id !== callId),
      }));
      setAssigningCallId(null);
      setAssignAgentId("");
    } catch (err) {
      const e = normalizeApiError(err);
      showToast(e.message || "Failed to assign call", true, 5000);
    } finally {
      setBusyKey("");
    }
  }

  const members = teamData?.members || [];
  const totalPages = Math.max(1, Math.ceil(members.length / TEAM_PAGE_SIZE));
  const pageStartIndex = (currentPage - 1) * TEAM_PAGE_SIZE;
  const paginatedMembers = members.slice(pageStartIndex, pageStartIndex + TEAM_PAGE_SIZE);

  const unassignedCalls = callsData?.items || [];
  const agents = callsData?.agents || [];

  const campaignOptions = (() => {
    const names = new Set();
    (teamData?.campaigns || []).forEach((c) => c?.name && names.add(c.name));
    members.forEach((m) => m?.assigned_campaign && names.add(m.assigned_campaign));
    return Array.from(names);
  })();

  return (
    <>
      {(teamError || callsError) && (
        <RetryBlock
          message="Could not load team data."
          onRetry={() => handleRefresh().catch(() => undefined)}
        />
      )}

      <PageHeader
        title="Team Management"
        subtitle="Manage calling agents and assign AI call outcomes for human follow-up."
        actions={
          <Button
            variant="gold"
            disabled={busyKey === "add-member"}
            onClick={() => {
              setOpenMenuMemberId("");
              formModal.open({ mode: "add", member: null });
            }}
          >
            Add Team Member
          </Button>
        }
      />

      {/* ── Calls Pending Assignment ─────────────────────────────────── */}
      {unassignedCalls.length > 0 ? (
      <div className="chart-card" style={{ marginBottom: "24px" }}>
        <div className="settings-panel-header" style={{ marginBottom: "16px" }}>
          <div className="team-card-heading">
            AI Calls Pending Assignment
            {unassignedCalls.length > 0 && (
              <span
                className="badge badge-warning"
                style={{ marginLeft: "8px", fontSize: "12px" }}
              >
                {unassignedCalls.length}
              </span>
            )}
          </div>
          <div className="page-subtitle">
            Completed AI calls with interested / callback dispositions or high lead score — assign to a human agent for follow-up.
          </div>
        </div>

        {unassignedCalls.length === 0 ? (
          <div className="empty-state" style={{ padding: "24px 0" }}>
            <div className="empty-title">No calls pending assignment</div>
            <div className="empty-sub">
              Calls with qualifying dispositions will appear here automatically.
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Disposition</th>
                  <th>Lead Score</th>
                  <th>Called At</th>
                  <th>Assign To</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {unassignedCalls.map((call) => {
                  const callId = call.call_id || call.id;
                  const isAssigning = assigningCallId === callId;
                  const isBusy = busyKey === `assign-${callId}`;
                  const contactName =
                    call.contact?.name || call.patient_name || call.contact_name || "Unknown";
                  const phone = call.contact?.phone || call.phone_number || "";

                  return (
                    <tr key={callId}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{contactName}</div>
                        {phone && (
                          <div className="text-muted" style={{ fontSize: "12px" }}>
                            {phone}
                          </div>
                        )}
                      </td>
                      <td>{dispositionBadge(call.disposition)}</td>
                      <td>
                        {call.lead_score != null ? (
                          <span className="badge badge-secondary">
                            {call.lead_score}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {call.started_at
                          ? new Date(call.started_at).toLocaleString([], {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </td>
                      <td>
                        {isAssigning ? (
                          <select
                            className="form-select"
                            style={{ fontSize: "13px", padding: "4px 8px" }}
                            value={assignAgentId}
                            onChange={(e) => setAssignAgentId(e.target.value)}
                          >
                            <option value="">Select agent…</option>
                            {agents.map((a) => (
                              <option key={a.agent_id || a.id} value={a.agent_id || a.id}>
                                {a.username || a.name || a.email}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-muted" style={{ fontSize: "12px" }}>
                            —
                          </span>
                        )}
                      </td>
                      <td>
                        {isAssigning ? (
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={isBusy || !assignAgentId}
                              onClick={() => handleAssign(callId)}
                            >
                              {isBusy ? "Assigning…" : "Confirm"}
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              disabled={isBusy}
                              onClick={() => {
                                setAssigningCallId(null);
                                setAssignAgentId("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => {
                              setAssigningCallId(callId);
                              setAssignAgentId("");
                            }}
                          >
                            Assign
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      ) : null}

      {/* ── Team Members Table ────────────────────────────────────────── */}
      <div className="chart-card">
        <div className="settings-panel-header" style={{ marginBottom: "16px" }}>
          <div className="team-card-heading">Team Members</div>
          <div className="page-subtitle">Add, edit and manage agent accounts.</div>
        </div>

        <div className="table-wrap team-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamLoading && !members.length ? (
                <tr>
                  <td colSpan="5">
                    <div className="empty-state">
                      <div className="empty-title">Loading team roster…</div>
                    </div>
                  </td>
                </tr>
              ) : !members.length ? (
                <tr>
                  <td colSpan="5">
                    <div className="empty-state">
                      <div className="empty-title">No team members yet</div>
                      <div className="empty-sub">
                        Click "Add Team Member" to get started.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedMembers.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{member.name}</div>
                      {member.email && (
                        <div className="text-muted" style={{ fontSize: "12px" }}>
                          {member.email}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{member.role}</div>
                      <div className="text-muted" style={{ fontSize: "12px" }}>
                        {member.assigned_campaign
                          ? `Assigned to ${member.assigned_campaign}`
                          : "General calling access"}
                      </div>
                    </td>
                    <td>
                      <Badge variant={getStatusBadgeVariant(member.status)}>
                        {member.status}
                      </Badge>
                    </td>
                    <td style={{ fontSize: "12px" }}>{member.last_active || "—"}</td>
                    <td className="team-actions-cell">
                      <TeamActionMenu
                        open={openMenuMemberId === member.id}
                        disabled={busyKey === member.id}
                        member={member}
                        onToggle={() => setOpenMenuMemberId(member.id)}
                        onClose={() => setOpenMenuMemberId("")}
                        onEdit={() => {
                          setOpenMenuMemberId("");
                          formModal.open({ mode: "edit", member });
                        }}
                        onAssignCampaign={() => {
                          setOpenMenuMemberId("");
                          assignModal.open(member);
                        }}
                        onToggleStatus={() => {
                          setOpenMenuMemberId("");
                          toggleStatusModal.open(member);
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {members.length > TEAM_PAGE_SIZE && (
            <div className="pagination team-table-pagination">
              <div>
                Showing {pageStartIndex + 1}–
                {Math.min(pageStartIndex + TEAM_PAGE_SIZE, members.length)} of{" "}
                {members.length} team members
              </div>
              <div className="page-actions">
                <button
                  className="page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <div className="team-pagination-status">
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  className="page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────── */}
      <TeamMemberFormModal
        open={formModal.isOpen}
        mode={formModal.data?.mode || "add"}
        member={formModal.data?.member || null}
        busy={busyKey === "add-member" || busyKey === "edit-member"}
        onClose={() => { if (!busyKey) formModal.close(); }}
        onSubmit={(payload) => {
          const formMode = formModal.data?.mode || "add";
          const member = formModal.data?.member || null;

          if (formMode === "edit" && member?.id) {
            runTeamMutation({
              key: "edit-member",
              action: () => updateTeamMember(member.id, payload),
              successMessage: `${payload.name} updated.`,
              afterSuccess: () => formModal.close(),
            });
            return;
          }

          runTeamMutation({
            key: "add-member",
            action: () => addTeamMember(payload),
            successMessage: `${payload.name} added to the team.`,
            resetPage: true,
            afterSuccess: () => formModal.close(),
          });
        }}
      />

      <AssignCampaignModal
        open={assignModal.isOpen}
        member={assignModal.data}
        campaigns={campaignOptions}
        busy={busyKey === "assign-campaign"}
        onClose={() => { if (busyKey !== "assign-campaign") assignModal.close(); }}
        onSubmit={(payload) => {
          if (!assignModal.data?.id) return;
          runTeamMutation({
            key: "assign-campaign",
            action: () => assignTeamCampaign(assignModal.data.id, payload),
            successMessage: `${assignModal.data.name} assigned to ${payload.campaignName}.`,
            afterSuccess: () => assignModal.close(),
          });
        }}
      />

      <ToggleStatusConfirmModal
        open={toggleStatusModal.isOpen}
        member={toggleStatusModal.data}
        busy={busyKey === "toggle-member-status"}
        onClose={() => { if (busyKey !== "toggle-member-status") toggleStatusModal.close(); }}
        onConfirm={() => {
          if (!toggleStatusModal.data?.id) return;
          const member = toggleStatusModal.data;
          const isActive = member.status?.toLowerCase() === "active";
          const newStatus = isActive ? "Offline" : "Active";
          runTeamMutation({
            key: "toggle-member-status",
            action: () => toggleTeamMemberStatus(member.id, newStatus),
            successMessage: `${member.name} ${isActive ? "deactivated" : "activated"}.`,
            afterSuccess: () => toggleStatusModal.close(),
          });
        }}
      />
    </>
  );
}
